import { Controller } from '@hotwired/stimulus'
// @ts-expect-error: @hotwired/turbo is not typed
import * as Turbo from '@hotwired/turbo'
// @ts-expect-error: el-transition is not typed
import { enter, leave } from 'el-transition'

// Connects to data-controller="modal"
//
// Takes 3 targets:
// - dialog: (required) the <dialog> element to be shown
// - form: (optional) the <form> element to be reset when the modal is closed
// - initialFocus: (optional) the element to be focused when the modal is opened
export default class extends Controller {
  static targets = [
    'dialog',
    'form',
    'initialFocus',
    'submitButton',
  ]

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly hasFormTarget: boolean
  declare readonly formTarget: HTMLFormElement
  declare readonly hasInitialFocusTarget: boolean
  declare readonly initialFocusTarget: HTMLElement
  declare readonly hasSubmitButtonTarget: boolean
  declare readonly submitButtonTarget: HTMLButtonElement

  static values = {
    dismissable: { type: Boolean, default: true }
  }

  declare dismissableValue: boolean

  dialogTargetConnected (): void {
    // If we add the html tag data-open-once-connected="true" to a modal dialog, the model will open as soon
    // as the dialog component is connected.
    if (this.dialogTarget.dataset.openOnceConnected) {
      this.openModal()
    }
  }

  open (event: Event): void {
    event.preventDefault()
    this.openModal()
  }

  openModal () {
    this.dialogTarget.showModal()

    enter(this.dialogTarget)

    document.body.classList.add('modal-open') // Locks scrolling

    this.dialogTarget.addEventListener('click', this.#onBackdropClick.bind(this))
    this.dialogTarget.addEventListener('cancel', this.#onCancel.bind(this))

    if (this.hasInitialFocusTarget) {
      this.initialFocusTarget.focus()
    }

    if (this.hasFormTarget && this.hasSubmitButtonTarget) {
      this.setupFormValidation()
    }
  }

  setupFormValidation () {
    this.formTarget.querySelectorAll('input, select, textarea').forEach(field => {
      field.addEventListener('input', () => this.validateForm())
    })

    this.validateForm()

    this.formTarget.addEventListener('submit', () => {
      this.submitButtonTarget.disabled = true
    })
  }

  validateForm () {
    if (this.hasFormTarget && this.hasSubmitButtonTarget) {
      const isValid = this.formTarget.checkValidity()
      this.submitButtonTarget.disabled = !isValid
    }
  }

  close (event: Event): void {
    // Prevent closing if modal is not dismissable
    if (!this.dismissableValue) {
      event.preventDefault()
      return
    }

    event.preventDefault()

    leave(this.dialogTarget).then(() => {
      this.dialogTarget.close()

      if (this.hasFormTarget) {
        this.formTarget.reset()
      }

      this.dialogTarget.removeEventListener('click', this.#onBackdropClick.bind(this))
      this.dialogTarget.removeEventListener('cancel', this.#onCancel.bind(this))

      document.body.classList.remove('modal-open') // Unlocks scrolling

      if (this.dialogTarget.dataset.redirectBackOnceClosed) {
        Turbo.visit(document.referrer || '/')
      }
    })
  }

  #onBackdropClick (event: MouseEvent): void {
    // Prevent backdrop close if modal is not dismissable
    if (!this.dismissableValue) {
      return
    }

    // This conditional is to avoid a bug that surfaces in modals
    // containing a form. When a user presses Enter in a form field
    // to submit the form, it triggers an implicit submission of the form.
    // Per the HTML5 spec (https://html.spec.whatwg.org/#implicit-submission),
    // this implicit submission causes the browser to fire a synthetic click
    // event on the submit button. The line below prevents synthetic clicks
    // (which appear to have clientX and clientY values of 0,0) from calling
    // close() which would block form submission through the call to
    // preventDefault in close().
    if (event.clientX === 0 && event.clientY === 0) {
      return
    }

    const rect = this.dialogTarget.getBoundingClientRect()

    const isInDialog = (
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width
    )

    if (!isInDialog) {
      this.close(event)
    }
  }

  #onCancel (event: Event): void {
    // Prevent ESC key from closing non-dismissable modals
    if (!this.dismissableValue) {
      event.preventDefault()
      return
    }

    // For dismissable modals, allow the default ESC behavior to close the modal
    this.close(event)
  }
}
